import { Contentful, MembershipTypes } from 'mxp-schemas';
import { CONSTANTS } from 'modules/app/constants';

interface SiteBannerConstantKeys {
  TITLE: string;
  DESCRIPTION: string;
  CTA_TEXT: string;
  CTA_URL: string;
}

export interface SiteBannerContent {
  title: string;
  description: string;
  ctaText: string;
  ctaURL: string;
}

interface SiteBannerContext {
  isLoggedOutBanner?: boolean;
  membershipBody?: string;
}

const emptyContent: SiteBannerContent = { title: '', description: '', ctaText: '', ctaURL: '' };

// Membership bodies with their own set of Contentful constants. Anything else
// (including a member with no membership body) falls back to LOGGED_IN_BANNER.
const membershipBodyBannerKeys: { [body: string]: SiteBannerConstantKeys } = {
  [MembershipTypes.MembershipBody.AICPA]: CONSTANTS.LOGGED_IN_BANNER_AICPA,
  [MembershipTypes.MembershipBody.CIMA]: CONSTANTS.LOGGED_IN_BANNER_CIMA,
};

const getMembershipBodyBannerKeys = (membershipBody?: string): SiteBannerConstantKeys | undefined =>
  membershipBody ? membershipBodyBannerKeys[membershipBody.trim().toUpperCase()] : undefined;

const readConstant = (constants: Contentful.Constants.Constants | null, key: string): string => {
  const value = constants?.[key];
  return typeof value === 'string' ? value : '';
};

const readBannerContent = (
  constants: Contentful.Constants.Constants | null,
  keys: SiteBannerConstantKeys
): SiteBannerContent => ({
  title: readConstant(constants, keys.TITLE),
  description: readConstant(constants, keys.DESCRIPTION),
  ctaText: readConstant(constants, keys.CTA_TEXT),
  ctaURL: readConstant(constants, keys.CTA_URL),
});

const hasContent = (content: SiteBannerContent): boolean =>
  Object.values(content).some(value => Boolean(value.trim()));

/**
 * Resolves the site banner content held in Contentful for the current visitor:
 * logged out -> logged out banner, logged in -> the banner for their membership body
 * when one is authored, otherwise the default logged in banner.
 */
export const getSiteBannerContent = (
  constants: Contentful.Constants.Constants | null,
  { isLoggedOutBanner, membershipBody }: SiteBannerContext = {}
): SiteBannerContent => {
  if (isLoggedOutBanner) return readBannerContent(constants, CONSTANTS.LOGGED_OUT_BANNER);

  const membershipBodyKeys = getMembershipBodyBannerKeys(membershipBody);
  const membershipBodyContent = membershipBodyKeys ? readBannerContent(constants, membershipBodyKeys) : emptyContent;

  return hasContent(membershipBodyContent)
    ? membershipBodyContent
    : readBannerContent(constants, CONSTANTS.LOGGED_IN_BANNER);
};
